// frontend/src/components/ActionPlans/ActionPlanTasksList.jsx
//
// Mixed list: action plans (header rows) with their actions (indented child rows).
// Filters affect both levels. Export to PDF and Excel.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { actionPlansAPI, usersAPI, ap_areasAPI, ap_subAreasAPI, ap_categoriesAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import SearchableSelect from '../Common/SearchableSelect';
import ActionPlanTaskModal from './ActionPlanTaskModal';
import RichTextEditor from '../Common/RichTextEditor';
import {
  AP_STATUS_META, getApStatusClass,
  ACTION_STATUS_META, getActionStatusClass,
} from '../../constants/statusMeta';
import {
  FiFilter, FiX, FiFileText, FiDownload, FiChevronDown, FiChevronRight,
  FiAlertCircle, FiCheckCircle, FiClock,
} from 'react-icons/fi';
import './ActionPlanTasksList.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '—';

const PLAN_TYPE_LABELS = {
  improvement: 'Melhoria',
  corrective:  'Correctiva',
  preventive:  'Preventiva',
  project:     'Projecto',
  incident:    'Incidente',
};

const PRIORITY_META = {
  LOW:      { label: 'Baixa',    cls: 'apl-priority--low' },
  MEDIUM:   { label: 'Média',    cls: 'apl-priority--medium' },
  HIGH:     { label: 'Alta',     cls: 'apl-priority--high' },
  CRITICAL: { label: 'Crítica',  cls: 'apl-priority--critical' },
};

// ── Component ─────────────────────────────────────────────────────────────────
const ActionPlanTasksList = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { t } = useTranslation();
  const { toasts, removeToast, toast_error } = useToast();

  // ── Data ────────────────────────────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);   // grouped: [{ plan, actions }]
  const [loading, setLoading] = useState(true);
  const [areas,    setAreas]    = useState([]);
  const [subAreas, setSubAreas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users,   setUsers]   = useState([]);

  // ── Filters ─────────────────────────────────────────────────────────────────
  // Filters restored from location.state when returning from detail
  const _saved = routeLocation.state?.aplFilters;
  const _initF = _saved?.filters || {
    status:'', delayed:'', planType:'', priority:'',
    areaId:'', categoryId:'', userId:'',
    search:'', targetDateFrom:'', targetDateTo:'',
  };
  const [filters,     setFilters]     = useState(_initF);
  const [searchDraft, setSearchDraft] = useState(_saved?.searchDraft || '');

  // Collapsed rows restored from state
  const [collapsed, setCollapsed] = useState(
    _saved?.collapsed ? new Set(_saved.collapsed) : new Set()
  );

  // ── Export state ─────────────────────────────────────────────────────────────
  const [exportingPdf,   setExportingPdf]   = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [taskModal, setTaskModal]               = useState(null);
  const [pendingTaskFiles, setPendingTaskFiles] = useState([]);

  // ── Load lookups once ────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      ap_areasAPI.getAll().then(r => setAreas(r.data?.data || r.data?.areas || [])),
      ap_subAreasAPI.getAll().then(r => setSubAreas(r.data?.data || r.data?.subAreas || [])),
      ap_categoriesAPI.getAll().then(r => setCategories(r.data?.data || r.data?.categories || [])),
      usersAPI.getAll().then(r => setUsers(r.data?.users || [])),
    ]).catch(() => {});
  }, []);

  // ── Load main data ───────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (Array.isArray(v) ? v.length : v) params.set(k, Array.isArray(v) ? v.join(',') : v);
      });
      const res = await actionPlansAPI.getTasksList(params.toString());
      if (res.data.success) {
        setRows(res.data.data || []);
      }
    } catch (err) {
      toast_error('Erro ao carregar lista');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // ── Filter helpers ───────────────────────────────────────────────────────────
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const commitSearch = () => setF('search', searchDraft);

  const clearFilters = () => {
    setFilters({
      planStatus: '', actionStatus: '', planType: '', priority: '',
      status: '', delayed: '', areaId: '', subAreaId: '', categoryId: '',
      search: '', targetDateFrom: '', targetDateTo: '',
    });
    setSearchDraft('');
  };

  const hasActiveFilters = Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : Boolean(v));

  // ── Toggle collapse ──────────────────────────────────────────────────────────
  const toggleCollapse = (planId) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(planId) ? next.delete(planId) : next.add(planId);
    return next;
  });

  // ── Excel export ─────────────────────────────────────────────────────────────
  // Excel export (backend download)
  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (Array.isArray(v) ? v.length : v) params.set(k, Array.isArray(v) ? v.join(',') : v);
      });
      const res = await actionPlansAPI.getTasksListExcel(params.toString());
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PlanosMelhoriaAccoes_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast_error('Erro ao exportar Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (Array.isArray(v) ? v.length : v) params.set(k, Array.isArray(v) ? v.join(',') : v);
      });
      const res = await actionPlansAPI.getTasksListPdf(params.toString());
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PlanosMelhoriaAccoes_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast_error('Erro ao gerar PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  const totalPlans   = rows.length;
  const totalActions = rows.reduce((s, r) => s + (r.actions?.length || 0), 0);

  const handleTaskSave = async (form) => {
    const { planPublicId, task } = taskModal;
    try {
      if (task?.public_id) { await actionPlansAPI.updateAction(task.public_id, form); }
      else                { await actionPlansAPI.addAction(planPublicId, form); }
      setTaskModal(null); setPendingTaskFiles([]); await load();
    } catch (err) { toast_error('Erro ao guardar tarefa'); throw err; }
  };

  return (
    <div className="apl-page">

      {/* ── Page header ── */}
      <div className="apl-header">
        <div className="apl-header-left">
          <h2 className="apl-title">Planos &amp; Acções</h2>
          <div className="apl-counts">
            <span className="apl-count-chip apl-count-chip--plan">{totalPlans} planos</span>
            <span className="apl-count-chip apl-count-chip--action">{totalActions} acções</span>
          </div>
        </div>
        <div className="apl-header-actions">
          <button
            className="btn-secondary apl-export-btn"
            onClick={handleExportExcel}
            disabled={exportingExcel || loading}
          >
            <FiDownload size={15} />
            {exportingExcel ? 'A exportar…' : 'Excel'}
          </button>
          <button
            className="btn-secondary apl-export-btn"
            onClick={handleExportPdf}
            disabled={exportingPdf || loading}
          >
            <FiFileText size={15} />
            {exportingPdf ? 'A gerar…' : 'PDF'}
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="apl-filters">
        <div className="apl-filters-row">

          {/* Search */}
          <div className="apl-filter-group apl-filter-group--wide">
            <label>Pesquisa</label>
            <div className="filter-search-wrap">
              <FiFilter size={13} className="filter-search-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Referência, título…"
                value={searchDraft}
                onChange={e => setSearchDraft(e.target.value)}
                onBlur={commitSearch}
                onKeyDown={e => e.key === 'Enter' && commitSearch()}
              />
            </div>
          </div>

          {/* Status — matches plan OR action status */}
          <div className="apl-filter-group">
            <label>Estado</label>
            <SearchableSelect
              value={filters.status}
              onChange={v => setF('status', v ?? '')}
              options={Object.entries(AP_STATUS_META).map(([k, m]) => ({ value: k, label: m.label }))}
              getOptionLabel={o => o.label} getOptionValue={o => o.value}
              formatDisplay={o => o.label} placeholder="Todos" isClearable
            />
          </div>

          {/* Delayed toggle */}
          <div className="apl-filter-group apl-filter-group--toggle">
            <label>Atrasados</label>
            <button
              className={`apl-delayed-btn${filters.delayed === 'true' ? ' apl-delayed-btn--active' : ''}`}
              onClick={() => setF('delayed', filters.delayed === 'true' ? '' : 'true')}
            >
              <FiAlertCircle size={13} />
              {filters.delayed === 'true' ? 'Apenas atrasados' : 'Todos'}
            </button>
          </div>

          {/* Plan type */}
          <div className="apl-filter-group">
            <label>Tipo</label>
            <SearchableSelect
              value={filters.planType}
              onChange={v => setF('planType', v ?? '')}
              options={Object.entries(PLAN_TYPE_LABELS).map(([k, l]) => ({ value: k, label: l }))}
              getOptionLabel={o => o.label} getOptionValue={o => o.value}
              formatDisplay={o => o.label} placeholder="Todos" isClearable
            />
          </div>

          {/* Priority */}
          <div className="apl-filter-group">
            <label>Prioridade</label>
            <SearchableSelect
              value={filters.priority}
              onChange={v => setF('priority', v ?? '')}
              options={Object.entries(PRIORITY_META).map(([k, m]) => ({ value: k, label: m.label }))}
              getOptionLabel={o => o.label} getOptionValue={o => o.value}
              formatDisplay={o => o.label} placeholder="Todas" isClearable
            />
          </div>

          {/* Area */}
          <div className="apl-filter-group">
            <label>Área</label>
            <SearchableSelect
              multi
              value={Array.isArray(filters.areaId) ? filters.areaId : (filters.areaId ? [filters.areaId] : [])}
              onChange={v => setF('areaId', v)}
              options={areas.map(a => ({ value: a.area_id, label: a.area_name }))}
              getOptionLabel={o => o.label} getOptionValue={o => String(o.value)}
              placeholder="Todas"
            />
          </div>

          {/* Sub-Area */}
          <div className="apl-filter-group">
            <label>Sub-Área</label>
            <SearchableSelect
              multi
              value={Array.isArray(filters.subAreaId) ? filters.subAreaId : (filters.subAreaId ? [filters.subAreaId] : [])}
              onChange={v => setF('subAreaId', v)}
              options={(() => {
                const sel = Array.isArray(filters.areaId) ? filters.areaId : (filters.areaId ? [String(filters.areaId)] : []);
                const list = sel.length ? subAreas.filter(s => sel.includes(String(s.area_id))) : subAreas;
                return list.map(s => ({ value: s.sub_area_id, label: s.sub_area_name }));
              })()}
              getOptionLabel={o => o.label} getOptionValue={o => String(o.value)}
              placeholder="Todas"
            />
          </div>

          {/* Category */}
          <div className="apl-filter-group">
            <label>Categoria</label>
            <SearchableSelect
              multi
              value={Array.isArray(filters.categoryId) ? filters.categoryId : (filters.categoryId ? [filters.categoryId] : [])}
              onChange={v => setF('categoryId', v)}
              options={categories.map(c => ({ value: c.category_id, label: c.category_name }))}
              getOptionLabel={o => o.label} getOptionValue={o => String(o.value)}
              placeholder="Todas"
            />
          </div>

          {/* Owner */}
          <div className="apl-filter-group">
            <label>Responsável</label>
            <SearchableSelect
              multi
              value={Array.isArray(filters.userId) ? filters.userId : (filters.userId ? [filters.userId] : [])}
              onChange={v => setF('userId', v)}
              options={users.map(u => ({ value: u.user_id, label: u.full_name || u.username }))}
              getOptionLabel={o => o.label} getOptionValue={o => String(o.value)}
              placeholder="Todos"
            />
          </div>

          {/* Date range */}
          <div className="apl-filter-group apl-filter-group--date">
            <label>Data Alvo De</label>
            <input type="date" className="form-control"
              value={filters.targetDateFrom}
              onChange={e => setF('targetDateFrom', e.target.value)} />
          </div>
          <div className="apl-filter-group apl-filter-group--date">
            <label>Data Alvo Até</label>
            <input type="date" className="form-control"
              value={filters.targetDateTo}
              onChange={e => setF('targetDateTo', e.target.value)} />
          </div>

          {/* Clear */}
          {hasActiveFilters && (
            <div className="apl-filter-group apl-filter-clear">
              <label>&nbsp;</label>
              <button className="btn-secondary btn-sm" onClick={clearFilters}
                style={{ display:'flex', alignItems:'center', gap:4, height:36 }}>
                <FiX size={13} /> Limpar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="apl-table-wrap">
        {loading ? (
          <div className="apl-loading">A carregar…</div>
        ) : rows.length === 0 ? (
          <div className="apl-empty">
            <FiFilter size={32} />
            <p>Nenhum resultado com os filtros actuais.</p>
          </div>
        ) : (
          <table className="apl-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th style={{ width: 120 }}>Referência</th>
                <th>Título / Descrição</th>
                <th style={{ width: 100 }}>Estado</th>
                <th style={{ width: 90 }}>Prioridade</th>
                <th style={{ width: 130 }}>Responsável</th>
                <th style={{ width: 96 }}>Data Alvo</th>
                <th style={{ width: 80 }}>Progresso</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ plan, actions }) => {
                const isCollapsed = collapsed.has(plan.action_plan_id);
                const hasActions  = actions?.length > 0;
                const planMeta    = AP_STATUS_META[plan.status] || AP_STATUS_META.NEW;
                const prio        = PRIORITY_META[plan.priority];

                return (
                  <React.Fragment key={plan.action_plan_id}>

                    {/* ── Plan row ── */}
                    <tr
                      className="apl-row apl-row--plan"
                      onClick={() => navigate(`/action-plans/${plan.public_id}`,
                      { state: { returnTo: '/action-plans/tasks',
                                 aplFilters: { filters, searchDraft, collapsed: [...collapsed] } } })}
                    >
                      <td className="apl-toggle" onClick={e => { e.stopPropagation(); if (hasActions) toggleCollapse(plan.action_plan_id); }}>
                        {hasActions
                          ? (isCollapsed ? <FiChevronRight size={14} /> : <FiChevronDown size={14} />)
                          : null}
                      </td>
                      <td>
                        <div className="apl-ref">{plan.reference_no || '—'}</div>
                        <div className="apl-type-chip">{PLAN_TYPE_LABELS[plan.plan_type] || plan.plan_type}</div>
                      </td>
                      <td>
                        <div className="apl-plan-title">{plan.title}</div>
                        {plan.area_name && (
                          <div className="apl-plan-meta">{plan.area_name}{plan.category_name ? ` · ${plan.category_name}` : ''}</div>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${getApStatusClass(plan.status)}`}>
                          {planMeta.label}
                        </span>
                      </td>
                      <td>
                        {prio && (
                          <span className={`apl-priority ${prio.cls}`}>{prio.label}</span>
                        )}
                      </td>
                      <td className="apl-user">{plan.owner_name || '—'}</td>
                      <td className="apl-date">{fmtDate(plan.target_date)}</td>
                      <td>
                        <div className="apl-progress">
                          <div className="apl-progress-bar">
                            <div className="apl-progress-fill"
                              style={{ width: `${plan.completion || 0}%` }} />
                          </div>
                          <span className="apl-progress-label">{plan.completion || 0}%</span>
                        </div>
                      </td>
                    </tr>

                    {/* ── Action rows ── */}
                    {!isCollapsed && (actions || []).map(action => {
                      const actionMeta = ACTION_STATUS_META[action.status] || ACTION_STATUS_META.NEW;
                      const isOverdue = action.target_date && new Date(action.target_date) < new Date()
                        && !['COMPLETED','CANCELLED'].includes(action.status);
                      return (
                        <tr key={action.action_id}
                          className={`apl-row apl-row--action${isOverdue ? ' apl-row--overdue' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={async (e) => { e.stopPropagation();
                            try {
                              const r = await actionPlansAPI.getActionById(action.action_id);
                              const full = r?.data?.success ? r.data.data : action;
                              setTaskModal({ task: full, planPublicId: plan.public_id });
                            } catch {
                              setTaskModal({ task: action, planPublicId: plan.public_id });
                            }
                          }}>
                          <td></td>
                          <td>
                            <span className="apl-seq">#{action.sequence_no || '—'}</span>
                          </td>
                          <td>
                            <div className="apl-action-desc"><RichTextEditor value={action.title || ''} readOnly minHeight={0} /></div>
                            {action.progress_notes && (
                              <div className="apl-action-notes">{action.progress_notes}</div>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${getActionStatusClass(action.status)}`}>
                              {actionMeta.label}
                            </span>
                          </td>
                          <td></td>
                          <td className="apl-user">{action.responsible_name || '—'}</td>
                          <td className={`apl-date${isOverdue ? ' apl-date--overdue' : ''}`}>
                            {fmtDate(action.target_date)}
                            {isOverdue && <FiAlertCircle size={12} className="apl-overdue-icon" />}
                          </td>
                          <td>
                            <div className="apl-progress">
                              <div className="apl-progress-bar">
                                <div className="apl-progress-fill"
                                  style={{ width: `${action.completion || 0}%` }} />
                              </div>
                              <span className="apl-progress-label">{action.completion || 0}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {taskModal && (
        <ActionPlanTaskModal
          task={taskModal.task?.public_id ? taskModal.task : null}
          planPublicId={taskModal.planPublicId}
          users={users}
          pendingFiles={pendingTaskFiles}
          onPendingFilesChange={setPendingTaskFiles}
          onSave={handleTaskSave}
          onCancel={() => { setTaskModal(null); setPendingTaskFiles([]); }}
        />
      )}

      {/* ── Toast ── */}
      <div className="comments-toast-container">
        {toasts.map(toast => (
          <Toast key={toast.id} message={toast.message} type={toast.type}
            duration={toast.duration} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </div>
  );
};

export default ActionPlanTasksList;